import React, { useEffect, useRef } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import Select from "components/ui/Select";
import useModalStore from "store/modals";
import { useAllPricelists } from "http-hooks/pricelist/usePricelist";
import { formatDateAndTime } from "utils/date";
import useAuth from "hooks/useAuth";

// Type labels for display
const typeLabels = {
  P: "Purchase",
  S: "Sales"
};

const SelectPriceLists = ({
  value,
  onChange,
  placeholder = "Select Pricelist",
  isDisabled = false,
  isRequired = false,
  label = "Pricelist",
  className = "",
  filters = {},
  showLabel = true,
  vendorId = null,
  showAddButton = true,
  autoSelectFirst = false,
}) => {
  const { agttp } = useAuth();
  const isTrader = agttp === 1;
  const { openModal } = useModalStore();
  const prevVendorIdRef = useRef(vendorId);
  
  const {
    isLoading: isLoadingPricelists,
    data: { data: allPricelists } = [],
  } = useAllPricelists({
    ...(vendorId && { supid: vendorId }),
    noPagination: true,
    ...filters,
  });

  useEffect(() => {
    if (!autoSelectFirst || !allPricelists || allPricelists.length === 0) return;
    // Auto-select first pricelist when vendor changes and options load
    if (prevVendorIdRef.current !== vendorId) {
      prevVendorIdRef.current = vendorId;
      if (vendorId) {
        onChange(allPricelists[0]);
      }
    }
  }, [autoSelectFirst, allPricelists, vendorId, onChange]);

  const handleAddNew = () => {
    openModal('pricelist');
  };

  // Custom label formatter for pricelists
  const getCustomLabel = React.useCallback((pricelist) => {
    if (!pricelist) return '';
    
    let label = "";
    
    // If no vendorId filter, show vendor name in label (using vendor name from pricelist data)
    if (!vendorId && pricelist.vendor) {
      label += `${pricelist.vendor?.name} | `;
    }
    
    // Show type for traders if it's Purchase or Sales (not General)
    if (isTrader && pricelist.typ && pricelist.typ !== 'G' && typeLabels[pricelist.typ]) {
      label += `${typeLabels[pricelist.typ]} | `;
    }
    
    // Add circular date and effective date
    label += `Circular: ${formatDateAndTime(pricelist.cirdt) || 'N/A'} | Effective: ${formatDateAndTime(pricelist.effrctivedt) || 'N/A'}`;
    
    return label;
  }, [vendorId, isTrader]);

  return (
    <div>
      {showLabel && (
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium leading-6 text-gray-900">
            {label}
          </label>
          {showAddButton && !isDisabled && (
            <button
              type="button"
              onClick={handleAddNew}
              className="ml-2 p-1 text-xs font-bold bg-black text-white rounded hover:bg-gray-800 transition-colors duration-200 flex items-center"
              title="Add new pricelist"
            >
              <PlusIcon className="h-3 w-3" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
      <Select
        value={value}
        onChange={onChange}
        options={allPricelists}
        getOptionValue={(option) => option?.rateid}
        getOptionLabel={getCustomLabel}
        placeholder={placeholder}
        isDisabled={isDisabled}
        isLoading={isLoadingPricelists}
        isRequired={isRequired}
        label={label}
        className={className}
        showLabel={false}
        isClearable={true}
        isSearchable={true}
      />
      {!showLabel && showAddButton && !isDisabled && (
        <button
          type="button"
          onClick={handleAddNew}
          className="absolute top-0 right-0 h-10 w-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors duration-200"
          title="Add new pricelist"
        >
          <PlusIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
};

export default SelectPriceLists; 